import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Play, CheckCircle, WarningCircle, MapPin, Clock, QrCode,
  Pulse,
} from "@phosphor-icons/react";

const SEV_STYLE = {
  high: "bg-[#F4C7B9] text-[#7A2E18] border-[#C25D38]/30",
  medium: "bg-[#F9E4B7] text-[#7A5A11] border-[#D4A418]/30",
  low: "bg-[#E5E2D9] text-[#5C5A55] border-[#C6C4BA]",
};
const TYPE_ICON = {
  "fraud.gps_deviation": MapPin,
  "fraud.idle": Clock,
  "fraud.missed_task": Clock,
  "fraud.manual_entry_ratio": QrCode,
};

export default function FraudDashboard() {
  const [rules, setRules] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const [r, a] = await Promise.all([
      api.get("/fraud/rules"), api.get("/fraud/audit?limit=200"),
    ]);
    setRules(r.data); setAlerts(a.data); setForm(r.data);
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const saveRules = async (e) => {
    e.preventDefault();
    try {
      await api.put("/fraud/rules", {
        gps_deviation_km: Number(form.gps_deviation_km),
        idle_minutes: Number(form.idle_minutes),
        missed_task_grace_minutes: Number(form.missed_task_grace_minutes),
        manual_entry_threshold_pct: Number(form.manual_entry_threshold_pct),
      });
      toast.success("Rules updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/fraud/run");
      const total = Object.values(r.data.raised).reduce((a, b) => a + b, 0);
      toast.success(total ? `${total} alerts raised` : "No violations detected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRunning(false);
  };

  const ack = async (id, status) => {
    try { await api.post(`/alerts/${id}/ack`, { status }); toast.success("Alert " + status); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const byType = alerts.reduce((a, e) => { a[e.type] = (a[e.type]||0)+1; return a; }, {});

  return (
    <AppShell title="Fraud & Control" subtitle="Module 11"
      actions={
        <div className="flex gap-2 items-center">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#2D5A27] animate-pulse"/>
            Auto-scan every 60s
          </div>
          <button onClick={runNow} disabled={running} data-testid="btn-fraud-run"
                  className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            <Play size={14} weight="bold"/>{running ? "Running…" : "Run detection now"}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="GPS deviations" value={byType["fraud.gps_deviation"] || 0} icon={MapPin}/>
        <Kpi label="Idle staff alerts" value={byType["fraud.idle"] || 0} icon={Clock}/>
        <Kpi label="Missed tasks" value={byType["fraud.missed_task"] || 0} icon={WarningCircle}/>
        <Kpi label="Manual entries" value={byType["fraud.manual_entry_ratio"] || 0} icon={QrCode}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
            <ShieldCheck size={18} color="#2D5A27" weight="fill"/>
            <div className="font-heading">Fraud alerts</div>
            <div className="flex-1"/>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{alerts.length} events</div>
          </div>
          <div className="divide-y divide-[#E5E2D9] max-h-[600px] overflow-y-auto" data-testid="fraud-alerts">
            {alerts.length === 0 && <div className="p-10 text-center text-sm text-[#8A8780]">No fraud alerts — all clean.</div>}
            {alerts.map(a => {
              const Icon = TYPE_ICON[a.type] || WarningCircle;
              return (
                <div key={a.id} className="px-6 py-4" data-testid={`fraud-alert-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded bg-[#F7F6F2] border border-[#E5E2D9] flex items-center justify-center shrink-0">
                      <Icon size={16} color="#C25D38" weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded border ${SEV_STYLE[a.severity]}`}>{a.severity}</span>
                        <span className="text-[11px] font-mono text-[#8A8780]">{a.type}</span>
                        <span className="text-[10px] text-[#8A8780] font-mono ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm mt-1.5 text-[#1A1A1A]">{a.message}</div>
                      {a.meta && Object.keys(a.meta).length > 0 && (
                        <div className="text-[11px] mt-1 font-mono text-[#8A8780] truncate">{JSON.stringify(a.meta)}</div>
                      )}
                      {a.status === "open" && (
                        <div className="flex gap-3 mt-2 text-xs">
                          <button onClick={() => ack(a.id, "acknowledged")} className="text-[#2D5A27]">Acknowledge</button>
                          <button onClick={() => ack(a.id, "closed")} className="text-[#C25D38]">Close</button>
                        </div>
                      )}
                      {a.status !== "open" && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mt-2">Status: {a.status}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6 h-max">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Detection rules</div>
          <div className="font-heading text-lg mb-5">Configure thresholds</div>
          {form && (
            <form onSubmit={saveRules} className="space-y-4" data-testid="fraud-rules-form">
              <Field label="GPS deviation (km)" step="0.1" value={form.gps_deviation_km}
                     onChange={v => setForm(f => ({ ...f, gps_deviation_km: v }))} testid="rule-gps"/>
              <Field label="Idle threshold (minutes)" value={form.idle_minutes}
                     onChange={v => setForm(f => ({ ...f, idle_minutes: v }))} testid="rule-idle"/>
              <Field label="Missed-task grace (minutes)" value={form.missed_task_grace_minutes}
                     onChange={v => setForm(f => ({ ...f, missed_task_grace_minutes: v }))} testid="rule-grace"/>
              <Field label="Manual QR entry threshold (%)" step="0.5" value={form.manual_entry_threshold_pct}
                     onChange={v => setForm(f => ({ ...f, manual_entry_threshold_pct: v }))} testid="rule-manual"/>
              <button type="submit" data-testid="rule-save"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold"/>Save rules
              </button>
            </form>
          )}
          <div className="mt-5 pt-5 border-t border-[#E5E2D9] text-xs text-[#5C5A55] leading-relaxed">
            <div className="font-medium text-[#1A1A1A] mb-1">How it works</div>
            Rules auto-run every 60s in the background.<br/>
            Alerts are de-duplicated within 1 hour.<br/>
            Manual detection can be triggered anytime.
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const Kpi = ({ label, value, icon: Icon }) => (
  <div className="stat-card">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded bg-[#C25D38]/10 flex items-center justify-center">
        <Icon size={16} color="#C25D38" weight="duotone"/>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
    </div>
    <div className="font-heading text-3xl mt-4">{value}</div>
  </div>
);
const Field = ({ label, value, onChange, step = "1", testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <input data-testid={testid} type="number" step={step} value={value}
           onChange={e => onChange(e.target.value)}
           className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
  </div>
);
